import { Pencil } from 'lucide-react'
import * as React from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export type InfoCardBorderColor = 'green' | 'amber' | 'blue' | 'purple' | 'red' | 'zinc'

type BorderColor = InfoCardBorderColor

/**
 * The tone marks the section through its icon tile only (no coloured left
 * edge): `green` is the brand/service mark and follows the nearest
 * [data-service] scope (forest by default), the others are status tints.
 */
const colorStyles: Record<BorderColor, { iconTile: string }> = {
  green: { iconTile: 'bg-service-tint text-service' },
  amber: { iconTile: 'bg-warning-tint text-warning' },
  blue: { iconTile: 'bg-info-tint text-info' },
  purple: { iconTile: 'bg-info-tint text-info' },
  red: { iconTile: 'bg-danger-tint text-danger' },
  zinc: { iconTile: 'bg-surface text-muted-foreground' },
}

interface InfoCardProps {
  title: string
  icon: React.ElementType
  /**
   * Replaces the icon tile with a mark of its own (a logo tile the size of the
   * icon tile, e.g. `InstitutionLogo`). The icon is then not drawn.
   */
  media?: React.ReactNode
  children: React.ReactNode
  onEdit?: () => void
  badgeText?: string
  badgeColor?: string
  borderColor?: BorderColor
  showEditButton?: boolean
}

export const InfoCard = ({
  title,
  icon: Icon,
  media,
  children,
  onEdit,
  badgeText,
  badgeColor = 'green',
  borderColor = 'green',
  showEditButton = true,
}: InfoCardProps) => {
  const styles = colorStyles[borderColor]

  return (
    <Card data-tone={borderColor} className="gap-0 p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {media ?? (
            <div
              data-slot="info-card-icon"
              className={cn('flex size-9 shrink-0 items-center justify-center rounded-control', styles.iconTile)}
            >
              <Icon className="size-5" />
            </div>
          )}
          <h3 className="type-title text-foreground">{title}</h3>
          {badgeText && (
            <Badge variant={badgeColor as React.ComponentProps<typeof Badge>['variant']} className="ml-2">
              {badgeText}
            </Badge>
          )}
        </div>
        {showEditButton && onEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        )}
      </div>
      {/* Content */}
      <div>{children}</div>
    </Card>
  )
}
